import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  LogIn, LogOut, Coffee, Menu, MapPin, Clock, AlertTriangle, CheckCircle2,
  Loader2, Fingerprint, CalendarDays, Navigation
} from 'lucide-react';
import {
  ATTENDANCE_TYPES, haversineDistance
} from '../mock';
import { apiClockIn, apiListAttendance, apiListWorkplaces } from '../api';
import { toast } from 'sonner';
import { logError } from '../lib/logger';

// Client-side helpers for UI state (validation is server-side)
const getAttendanceStatus = (events) => {
  if (!events || events.length === 0) return 'none';
  const last = events[events.length - 1];
  if (last.type === 'entrata' || last.type === 'pausa_fine') return 'working';
  if (last.type === 'pausa_inizio') return 'on_break';
  if (last.type === 'uscita') return 'done';
  return 'none';
};

const getAllowedActions = (events) => {
  const status = getAttendanceStatus(events);
  switch (status) {
    case 'none':    return { entrata: true,  uscita: false };
    case 'working': return { entrata: false, uscita: true  };
    case 'on_break':return { entrata: false, uscita: true  };
    case 'done':    return { entrata: false, uscita: false };
    default: return { entrata: false, uscita: false };
  }
};

const findNearest = (lat, lng, wps) => {
  const list = (wps || []).filter(w => w.active);
  let best = null;
  for (const wp of list) {
    const d = haversineDistance(lat, lng, wp.lat, wp.lng);
    if (!best || d < best.distance) best = { workplace: wp, distance: d };
  }
  if (!best) return null;
  const radius = best.workplace.radius || 100;
  return { ...best, inRange: best.distance <= radius, radius };
};

const STATUS_META = {
  none:    { label: 'Non ancora in servizio', color: 'slate', dot: 'bg-slate-400' },
  working: { label: 'Presente', color: 'emerald', dot: 'bg-emerald-500 animate-pulse' },
  on_break:{ label: 'In pausa', color: 'amber', dot: 'bg-amber-500 animate-pulse' },
  done:    { label: 'Giornata conclusa', color: 'blue', dot: 'bg-blue-500' }
};

const formatTime = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
const formatDate = (iso) => new Date(iso).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

// Time/duration constants (ms)
const CLOCK_TICK_INTERVAL_MS = 30_000;      // refresh "now" every 30s
const HISTORY_WINDOW_DAYS = 30;
const MS_PER_DAY = 86_400_000;
const HISTORY_WINDOW_MS = HISTORY_WINDOW_DAYS * MS_PER_DAY;
const GPS_CACHE_MAX_AGE_MS = 60_000;        // 1 min cache for passive GPS hint
const GPS_HINT_TIMEOUT_MS = 10_000;
const CLOCK_IN_GPS_TIMEOUT_MS = 15_000;
const MS_PER_HOUR = 3_600_000;
// Browser Geolocation API error codes
const GEO_ERR_PERMISSION_DENIED = 1;
const GEO_ERR_TIMEOUT = 3;

const AttendancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [now, setNow] = useState(new Date());
  const [loadingAction, setLoadingAction] = useState(null);
  const [gpsHint, setGpsHint] = useState(null);

  useEffect(() => {
    Promise.all([apiListAttendance(), apiListWorkplaces()])
      .then(([atts, wps]) => {
        setAllEvents(atts || []);
        setWorkplaces(wps || []);
      })
      .catch((e) => logError('Failed to load attendance:', e));
    const t = setInterval(() => setNow(new Date()), CLOCK_TICK_INTERVAL_MS);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todayEvents = useMemo(
    () => allEvents
      .filter(e => e.employeeEmail === user?.email && e.timestamp.startsWith(new Date().toISOString().slice(0, 10)))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
    [allEvents, user, now]
  );

  const status = getAttendanceStatus(todayEvents);
  const allowed = getAllowedActions(todayEvents);
  const statusMeta = STATUS_META[status];

  // Monthly events (last 30 days, for this user)
  const monthlyEvents = useMemo(() => {
    const cutoff = Date.now() - HISTORY_WINDOW_MS;
    return allEvents
      .filter(e => e.employeeEmail === user?.email && new Date(e.timestamp).getTime() >= cutoff)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [allEvents, user]);

  // Group monthly by day
  const monthlyByDay = useMemo(() => {
    const groups = {};
    for (const e of monthlyEvents) {
      const day = e.timestamp.slice(0, 10);
      if (!groups[day]) groups[day] = [];
      groups[day].push(e);
    }
    Object.values(groups).forEach(arr => arr.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    return groups;
  }, [monthlyEvents]);
  // Past days entries (excludes today) — memoized to avoid re-filtering every render
  const pastDaysEntries = useMemo(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    return Object.entries(monthlyByDay).filter(([day]) => day !== todayKey);
  }, [monthlyByDay]);

  // Live GPS check (passive) for user hint
  const checkGpsHint = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const res = findNearest(pos.coords.latitude, pos.coords.longitude, workplaces);
        if (res) {
          setGpsHint({
            distance: Math.round(res.distance),
            workplaceName: res.workplace.name,
            inRange: res.inRange,
            radius: res.radius
          });
        } else {
          setGpsHint({ noWorkplace: true });
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: GPS_HINT_TIMEOUT_MS, maximumAge: GPS_CACHE_MAX_AGE_MS }
    );
  }, [workplaces]);

  useEffect(() => { if (workplaces.length > 0) checkGpsHint(); }, [workplaces, checkGpsHint]);

  const handleAction = async (type) => {
    if (!allowed[type] || loadingAction) return;
    setLoadingAction(type);

    if (!navigator.geolocation) {
      toast.error('Geolocalizzazione non supportata dal tuo browser');
      setLoadingAction(null);
      return;
    }

    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: CLOCK_IN_GPS_TIMEOUT_MS,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = pos.coords;

      try {
        const record = await apiClockIn({ type, lat: latitude, lng: longitude });
        setAllEvents(prev => [record, ...prev]);
        toast.success(`${ATTENDANCE_TYPES[type].label} registrata alle ${formatTime(record.timestamp)} — ${record.workplaceName}`);
        setGpsHint({
          distance: record.distance,
          workplaceName: record.workplaceName,
          inRange: true,
          radius: 100
        });
      } catch (apiErr) {
        const detail = apiErr?.response?.data?.detail || 'Errore durante la timbratura';
        toast.error(detail, { duration: 6000 });
        // Update GPS hint with live distance
        const res = findNearest(latitude, longitude, workplaces);
        if (res) {
          setGpsHint({
            distance: Math.round(res.distance),
            workplaceName: res.workplace.name,
            inRange: res.inRange,
            radius: res.radius
          });
        }
      }
    } catch (err) {
      if (err.code === GEO_ERR_PERMISSION_DENIED) {
        toast.error('Permesso di geolocalizzazione negato. Abilitalo nelle impostazioni del browser per timbrare.');
      } else if (err.code === GEO_ERR_TIMEOUT) {
        toast.error('Timeout GPS. Riprova spostandoti in un\'area con segnale migliore.');
      } else {
        toast.error('Errore nel rilevamento della posizione. Riprova.');
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const sidebarProps = {
    chats: [], activeChatId: null,
    onNewChat: () => navigate('/'),
    onSelectChat: (id) => navigate(`/chat/${id}`),
    onDeleteChat: () => {},
    isOpen: sidebarOpen,
    onClose: () => setSidebarOpen(false)
  };

  const ActionButton = ({ type, icon: Icon, colorClasses }) => {
    const meta = ATTENDANCE_TYPES[type];
    const isAllowed = allowed[type];
    const isLoading = loadingAction === type;
    return (
      <button
        onClick={() => handleAction(type)}
        disabled={!isAllowed || !!loadingAction}
        className={`
          group relative flex flex-col items-center justify-center gap-2 p-5 rounded-2xl border-2 transition-all
          ${isAllowed ? `${colorClasses} hover:scale-[1.02] active:scale-[0.98] shadow-sm cursor-pointer` : 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60'}
        `}
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isAllowed ? 'bg-white/70' : 'bg-slate-100'}`}>
          {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Icon className="w-6 h-6" />}
        </div>
        <span className="text-sm font-semibold">{meta.label}</span>
      </button>
    );
  };

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden">
      {sidebarOpen && <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <Sidebar {...sidebarProps} />

      <main className="flex-1 flex flex-col min-w-0 bg-slate-50/30">
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-700 flex items-center justify-center">
                <Fingerprint className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Timbrature</div>
                <div className="text-xs text-slate-500">Presenze con geolocalizzazione</div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scroll">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6 space-y-5">

            {/* Status card */}
            <div className={`p-5 rounded-2xl border bg-white shadow-sm border-${statusMeta.color}-200`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className={`w-3 h-3 rounded-full ${statusMeta.dot}`} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Stato attuale</div>
                    <div className="text-lg font-semibold text-slate-900">{statusMeta.label}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 capitalize">{formatDate(new Date())}</div>
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">
                    {now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>

              {/* GPS hint */}
              {gpsHint && !gpsHint.noWorkplace && (
                <div className={`mt-4 p-3 rounded-lg flex items-start gap-2.5 text-sm ${
                  gpsHint.inRange ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-amber-50 border border-amber-200 text-amber-900'
                }`}>
                  <Navigation className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {gpsHint.inRange ? (
                      <>Sei a <strong>{gpsHint.distance}m</strong> da <strong>{gpsHint.workplaceName}</strong> — puoi timbrare ✓</>
                    ) : (
                      <>Sei a <strong>{gpsHint.distance}m</strong> da <strong>{gpsHint.workplaceName}</strong>. Devi essere entro <strong>{gpsHint.radius}m</strong> per timbrare.</>
                    )}
                  </div>
                  <button onClick={checkGpsHint} className="text-xs underline hover:no-underline">
                    Aggiorna
                  </button>
                </div>
              )}
              {gpsHint?.noWorkplace && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-900 flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Nessuna sede aziendale configurata. Contatta l'ufficio HR.
                </div>
              )}
              {!gpsHint && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4" />
                  Rilevamento posizione in corso... Assicurati di aver dato il permesso al browser.
                  <button onClick={checkGpsHint} className="ml-auto text-xs underline">Riprova</button>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 px-1">
                Azioni timbratura
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ActionButton type="entrata" icon={LogIn} colorClasses="bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100" />
                <ActionButton type="uscita" icon={LogOut} colorClasses="bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200" />
              </div>
              <p className="text-xs text-slate-500 mt-3 px-1 flex items-center gap-1.5">
                <MapPin className="w-3 h-3" />
                Per timbrare devi trovarti entro 100m da una sede aziendale attiva. La pausa pranzo di 1 ora viene conteggiata automaticamente nel calcolo delle ore lavorate.
              </p>
            </div>

            {/* Today's timeline */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 px-1">
                Timbrature di oggi
              </div>
              {todayEvents.length === 0 ? (
                <div className="p-6 text-center bg-white rounded-xl border border-dashed border-slate-300">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-600">Nessuna timbratura registrata oggi</div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {todayEvents.map(e => {
                    const meta = ATTENDANCE_TYPES[e.type];
                    const Icon = { LogIn, LogOut, Coffee }[meta.icon] || Clock;
                    return (
                      <div key={e.id} className="flex items-center gap-3 p-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${meta.color}-100 text-${meta.color}-700`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-slate-900">{meta.label}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {e.workplaceName} • {e.distance}m
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-base text-slate-900 tabular-nums">{formatTime(e.timestamp)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Monthly history */}
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Cronologia ultimi 30 giorni
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {Object.keys(monthlyByDay).length} {Object.keys(monthlyByDay).length === 1 ? 'giorno' : 'giorni'} lavorati
                </div>
              </div>
              {Object.keys(monthlyByDay).length === 0 ? (
                <div className="p-6 text-center bg-white rounded-xl border border-dashed border-slate-300">
                  <div className="text-sm text-slate-500">Nessuna timbratura nell'ultimo mese</div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {pastDaysEntries
                    .map(([day, events]) => {
                      const d = new Date(day);
                      const dateLabel = d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });
                      const first = events.find(e => e.type === 'entrata');
                      const last = events.reverse().find(e => e.type === 'uscita') || events[events.length - 1];
                      const dur = first && last ? (new Date(last.timestamp) - new Date(first.timestamp)) / MS_PER_HOUR : null;
                      return (
                        <div key={day} className="flex items-center gap-3 p-3">
                          <div className="w-12 flex-shrink-0">
                            <div className="text-[11px] text-slate-500 uppercase font-medium">{d.toLocaleDateString('it-IT', { weekday: 'short' })}</div>
                            <div className="text-base font-bold text-slate-900 tabular-nums">{d.getDate()}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 capitalize">{dateLabel}</div>
                            <div className="text-xs text-slate-500">{events.length} eventi • {first?.workplaceName}</div>
                          </div>
                          {dur !== null && dur > 0 && (
                            <div className="text-right">
                              <div className="text-xs text-slate-500">Ore</div>
                              <div className="text-sm font-semibold text-slate-900 tabular-nums">{dur.toFixed(1)}h</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AttendancePage;
